import { CloseOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { TooltipSparkline } from 'features/knowledgeDocumentViewer/components/viewer/shared/TooltipSparkline'
import { useInstrumentTimeSeriesData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import { ExternalLink, LineChart } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const Panel = styled.div<{ $width: number }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${(props) => props.$width}px;
  color: ${(props) => props.theme.forecast.defaultText};
  border-left: 1px solid ${(props) => props.theme.forecast.border};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0.75rem;
`

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`

const SparklineArea = styled.div`
  padding: 0.5rem 0.75rem;
`

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 0.75rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const NoDataIcon = styled(LineChart)`
  opacity: 0.4;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0.75rem;
`

const MetadataContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0.75rem;
`

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  padding: 1rem;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.forecast.cardGroupBackground} 50%,
    transparent
  );
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const PrimaryActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

const TabPickerOverlay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 0.375rem;
  background: ${(props) => props.theme.forecast.pageBackground1};
  max-height: 10rem;
  overflow-y: auto;
`

const TabOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.375rem 0.5rem;
  background: none;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.defaultText};
  text-align: left;

  &:hover {
    background-color: ${(props) => props.theme.forecast.hoverButton};
  }
`

const TabPickerLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${(props) => props.theme.forecast.secondaryText};
  padding: 0.25rem 0.5rem 0.125rem;
`

interface SensorPreviewPanelProps {
  sensor: SensorSearchItem | null
  isOpen: boolean
  onClose: () => void
  onOpenDashboard: (sensor: SensorSearchItem) => void
  onOpenNewTab?: (sensor: SensorSearchItem) => void
  onAddToContext?: (sensor: SensorSearchItem) => void
  openTabs: DocumentTab[]
  onAddToExistingTab?: (sensor: SensorSearchItem, tabId: string) => void
}

export function SensorPreviewPanel({
  sensor,
  isOpen,
  onClose,
  onOpenDashboard,
  onOpenNewTab,
  openTabs,
  onAddToExistingTab,
}: SensorPreviewPanelProps) {
  const [showTabPicker, setShowTabPicker] = useState(false)
  const PANEL_WIDTH = 500

  const sensorIds = useMemo(() => (sensor ? [sensor.sensorId] : []), [sensor])

  const { sparklineData, isLoading: isLoadingSparkline } =
    useInstrumentTimeSeriesData(sensorIds, isOpen && !!sensor)

  const sparkline = sparklineData[0] ?? null

  const dashboardTabs = useMemo(
    () =>
      openTabs.filter((tab) => {
        const current = tab.history[tab.currentIndex]
        // Saved-chart tabs render from the resolved chart config and ignore
        // sensor-id seeding, so they are not valid "add sensor here" targets.
        return current.type === 'timeseries' && !current.chartID
      }),
    [openTabs]
  )

  const handleAddToExisting = useCallback(
    (tabId: string) => {
      if (sensor && onAddToExistingTab) {
        onAddToExistingTab(sensor, tabId)
      }
      setShowTabPicker(false)
    },
    [sensor, onAddToExistingTab]
  )

  if (!isOpen || !sensor) {
    return null
  }

  return (
    <Panel $width={PANEL_WIDTH}>
      <Header>
        <TitleArea>
          <Text
            as='h3'
            variant='label'
            weight='semibold'
            truncate
            title={sensor.name}>
            {sensor.name}
          </Text>
          {sensor.description && (
            <Text
              as='p'
              variant='caption'
              color='muted'
              truncate
              title={sensor.description}>
              {sensor.description}
            </Text>
          )}
        </TitleArea>
        <Button
          variant='ghost'
          size='icon-lg'
          onClick={onClose}
          aria-label='Close preview'>
          <CloseOutlined />
        </Button>
      </Header>

      {isLoadingSparkline ? (
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      ) : sparkline ? (
        <SparklineArea>
          <TooltipSparkline
            piTag={sparkline.piTag}
            data={sparkline.data}
            currentValue={sparkline.currentValue}
            previousValue={sparkline.previousValue}
            lastMeasuredAt={sparkline.lastMeasuredAt}
            unit={sparkline.unit}
          />
        </SparklineArea>
      ) : (
        <NoDataContainer>
          <NoDataIcon size={32} />
          <Text as='p' size='sm'>
            No recent data
          </Text>
        </NoDataContainer>
      )}

      <MetadataContainer>
        <MetadataItem>
          <Text as='p' variant='caption' color='muted' leading='tight'>
            Unit
          </Text>
          <Text as='p' size='sm'>
            {sensor.unit || '-'}
          </Text>
        </MetadataItem>
        <MetadataItem>
          <Text as='p' variant='caption' color='muted' leading='tight'>
            Asset
          </Text>
          <Text as='p' size='sm'>
            {sensor.asset || '-'}
          </Text>
        </MetadataItem>
      </MetadataContainer>

      <ActionButtons>
        <PrimaryActions>
          <Button
            variant='primaryRaised'
            size='lg'
            onClick={() => onOpenDashboard(sensor)}>
            Open
          </Button>
          {onOpenNewTab && (
            <Button
              variant='inputRaised'
              size='lg'
              onClick={() => onOpenNewTab(sensor)}>
              Open In New Tab
              <ExternalLink />
            </Button>
          )}
        </PrimaryActions>
        {dashboardTabs.length > 0 && onAddToExistingTab && (
          <>
            <Button
              variant='ghost'
              size='lg'
              onClick={() => setShowTabPicker((prev) => !prev)}>
              Add to existing tab
            </Button>
            {showTabPicker && (
              <TabPickerOverlay>
                <TabPickerLabel>Select dashboard</TabPickerLabel>
                {dashboardTabs.map((tab) => {
                  const current = tab.history[tab.currentIndex]
                  const label =
                    current.type === 'timeseries' ? current.label : 'Dashboard'
                  return (
                    <TabOption
                      key={tab.id}
                      onClick={() => handleAddToExisting(tab.id)}>
                      <LineChart size={12} />
                      {label}
                    </TabOption>
                  )
                })}
              </TabPickerOverlay>
            )}
          </>
        )}
        {/* Temporarily hidden: Add to Agent Context button
        {onAddToContext && (
          <Button
            variant='ghost'
            size='lg'
            onClick={() => onAddToContext(sensor)}>
            <Plus />
            Add to Agent Context
          </Button>
        )}
        */}
      </ActionButtons>
    </Panel>
  )
}
